import cookie from 'cookie';
import setCookieParser from 'set-cookie-parser';
import { SET_NEED_SADUKA_AUTH_UPDATE } from 'cityads-v3/actions/auth';
import config from 'cityads-v3/config';
import { ensureArray } from 'cityads-v3/utils/arrayUtils';
import { SET_BACKEND_TIMEOUT } from 'cityads-v3/actions/app';

export const HTTP_STATUS_OK = 200;
export const HTTP_STATUS_MOVED_PERMANENTLY = 301;
export const HTTP_STATUS_FOUND = 302;
export const HTTP_STATUS_FORBIDDEN = 403;
export const HTTP_STATUS_NOT_FOUND = 404;
export const HTTP_STATUS_NO_CONTENT = 204;
export const HTTP_STATUS_SERVER_ERROR_START = 500;
export const HTTP_STATUS_SERVER_ERROR_UNAVAILABLE = 503
export const HTTP_STATUS_SERVER_ERROR_TIMEOUT = 504;
export const HTTP_STATUS_SERVER_ERROR_END = 599;

export const HTTP_METHOD_GET    = 'GET';
export const HTTP_METHOD_HEAD   = 'HEAD';
export const HTTP_METHOD_POST   = 'POST';
export const HTTP_METHOD_PUT    = 'PUT';
export const HTTP_METHOD_DELETE = 'DELETE';
export const HTTP_METHOD_PATCH  = 'PATCH';

export const MAX_FILE_SIZE = 7*1024*1024; // eslint-disable-line no-magic-numbers

const READONLY_METHODS = [HTTP_METHOD_GET, HTTP_METHOD_HEAD];
const XSRF_COOKIE_NAME = 'XSRF-TOKEN';
const XSRF_HEADER_NAME = 'X-CSRF-TOKEN';

const TRY_NUMBER = 1;

export const BACKEND_FETCH_TIMEOUT = config.BACKEND_FETCH_TIMEOUT;
export const SADUKA_TRANSPORT_PREFIX = config.USE_SADUKA_TRANSPORT ? config.SADUKA_TRANSPORT_URL : '/saduka';

export function checkStatus(response) {
  if (!response.ok) {
    // (response.status < 200 || response.status > 300)
    const error = new Error(response.statusText);
    error.response = response;
    throw error;
  }
  return response;
}

export function parseJSON(response) {
  if (response.status === HTTP_STATUS_NO_CONTENT) {
    return {};
  }
  return response.json();
}

export function getLanguageHeaders(language) {
  const browserLanguages = navigator ?
    (navigator.languages ?
      navigator.languages :
      (navigator.language ?
        [navigator.language] :
        (navigator.userLanguage ? [navigator.userLanguage] : [])
      )
    ) :
    [];

  return language ?
    {
      'Accept-Language': [language, ...browserLanguages.filter(item => item !== language)]
        .map((lang, num) => lang + ';q=' + (1 - num/10))
        .join(',')
    } :
    {};
}

export function makeQueryConfig(url, config, getState) {
  const lang = getState && getState().language.language;
  const apiHeaders = (!navigator && getState && getState().auth.apiHeaders) || {};
  const amplitudeInstance = window?.amplitude?.getInstance();
  const deviceId = amplitudeInstance?.options?.deviceId;
  const sessionId = amplitudeInstance?._sessionId;

  const isSameSite = !url.match(/^https?:/);
  const amplitudeHeaders = isSameSite ? {
    ...(deviceId ? { 'X-Amplitude-Device-Id': deviceId } : {}),
    ...(sessionId ? { 'X-Amplitude-Session-Id': sessionId } : {}),
  } : {};

  const isRequestReadonly = READONLY_METHODS.indexOf((config?.method || HTTP_METHOD_GET).toUpperCase()) >= 0;
  const xsrfHeaders = isRequestReadonly ?
    {} :
    {
      [XSRF_HEADER_NAME]: cookie.parse(document.cookie)[XSRF_COOKIE_NAME] || ''
    }
  ;

  return {
    ...config,
    headers: {
      ...apiHeaders,
      ...config?.headers,
      ...getLanguageHeaders(lang),
      ...amplitudeHeaders,
      ...xsrfHeaders,
    }
  }
}

/**
 * Для работы с redux-entities
 */
export function fetchApi(
  url,
  config,
  onRequestSuccess,
  onRequestFailure,
  { toJson = true } = {}
){
  return (dispatch, getState, extraArgument) => {
    let query;
    const controller = new AbortController();
    config = makeQueryConfig(url, config, getState);
    if (typeof window === 'undefined') {
      config.signal = controller.signal;
    }

    query = fetch(url, config);

    if (!navigator) {
      fetchTimeout(BACKEND_FETCH_TIMEOUT, controller);
    }

    return query
      .then(checkStatus)
      .then(response => toJson ? parseJSON(response) : response.text())
      .then(json => {
        if (onRequestSuccess) {
          onRequestSuccess(json);
        }

        return json;
      })
      .catch(error => {
        const response = error.response;

        if (controller?.signal?.aborted) {
          dispatch({
            type: SET_BACKEND_TIMEOUT
          });
        }

        if (response === undefined) {
          throw error;
        } else {
          let anotherError = {
            ...error,
            status: response.status,
            statusText: response.statusText,
          };

          return response.text().then(text => {
            try {
              const json = JSON.parse(text);
              anotherError.data = json;
              anotherError.message = json.message || 'Error requesting API';
            } catch (ex) {
              anotherError.message = anotherError.data = text;
            }

            throw anotherError;
          });
        }
      })
      .catch(error => {
        if (onRequestFailure) {
          onRequestFailure(error);
        }

        throw error;
      })
    ;
  }
}

/**
 * A utility to call a restful service.
 *
 * @param url The restful service end point.
 * @param config The config object of the call. Can be null.
 * @param request The request action.
 * @param onRequestSuccess The callback function to create request success action.
 *                 The function expects response json payload as its argument.
 * @param onRequestFailure The callback function to create request failure action.
 *                 The function expects error as its argument.
 */
export function callApi(
  url,
  config,
  request,
  onRequestSuccess,
  onRequestFailure,
  { toJson = true } = {}
) {
  let tryNumber = 0;
  const fetchQuery = (dispatch, getState, extraArgument) => {
    let query;
    const controller = new AbortController();
    const fetchConfig = makeQueryConfig(url, config, getState);

    if (!config?.signal) {
      fetchConfig.signal = controller.signal;
    }

    if (request) {
      dispatch(request);
    }

    query = fetch(url, fetchConfig);

    if( !navigator ) {
      fetchTimeout(BACKEND_FETCH_TIMEOUT, controller);
    }

    return query
      .then(checkStatus)
      .then(response => toJson ? parseJSON(response) : response.text())
      .catch(error => {
        throw error
      })
      .then(json => {
        if (onRequestSuccess) {
          dispatch(onRequestSuccess(json));
        }

        return json;
      })
      .catch(error => {
        const response = error.response;

        if (controller?.signal?.aborted) {
          dispatch({
            type: SET_BACKEND_TIMEOUT
          });
        }

        if (response === undefined) {
          throw error;
        } else {

          if (
            response.status >= HTTP_STATUS_SERVER_ERROR_START
            && response.status <= HTTP_STATUS_SERVER_ERROR_END
            && ++tryNumber < TRY_NUMBER
          ) {
            fetchQuery(dispatch, getState, extraArgument);
          }

          let anotherError = {
            ...error,
            status: response.status,
            statusText: response.statusText,
          };

          return response.text().then(text => {
            try {
              const json = JSON.parse(text);
              anotherError.data = json;
              anotherError.message = json.message || 'Error requesting API';
            } catch (ex) {
              anotherError.message = anotherError.data = text;
            }

            throw anotherError;
          });
        }
      })
      .catch(error => {
        if (onRequestFailure) {
          dispatch(onRequestFailure(error));
        }

        throw error;
      })
    ;
  };

  return fetchQuery;
}

function apiCbRequest(type, additional) {
  return function (dispatch, getState) {
    const types = ensureArray(type);

    types.forEach(type => dispatch({
      type,
      additional,
    }));
  }
}

function apiCb(type, additional) {
  return function(response) {
    if (
      navigator &&
      response &&
      response.status && response.status === HTTP_STATUS_FORBIDDEN &&
      !(additional && additional.apiOptions && additional.apiOptions.ignore403)
    ) {
      return {
        type: SET_NEED_SADUKA_AUTH_UPDATE
      };
    }

    return function (dispatch, getState) {
      const types = ensureArray(type);

      types.forEach(type => dispatch({
        type,
        response,
        payload: response,
        additional,
      }));
    }
  }
}

export function makeRequest(url, params) {
  const urlQuery = (url.indexOf('https:') === 0 || url.indexOf('http:') === 0) ? url : config.SADUKA_URL + url;

  return callApi(
    urlQuery,
    params.options ? {
      ...params.options,
      headers: params.options.headers || {},
    } : null,
    params?.types?.[0] ? apiCbRequest(params.types[0], params) : null,
    params?.types?.[1] ? apiCb(params.types[1], params) : null,
    params?.types?.[2] ? apiCb(params.types[2], params) : null,
    params.apiOptions
  );
}

export function fetchRequest(url, params, thunk) {
  const urlQuery = (url.indexOf('https:') === 0 || url.indexOf('http:') === 0) ? url : config.SADUKA_URL + url;

  return new Promise((resolve, reject) => {
    fetchApi(
      urlQuery,
      params.options ? {
        ...params.options,
        headers: params.options.headers || {},
      } : null,
      resolve,
      reject,
      params.apiOptions
    )(thunk.dispatch, thunk.getState);
  });
}

export function mapObjectize(items, idName = 'id', children = 'items', copyObject = true) {
  const obj = {};
  const func = (item) => {
    const value = item[idName];
    if (Array.isArray(value)) {
      for(let i = 0; i < value.length; i++) {
        obj[value[i]] = copyObject ? {...item} : item;
      }
    } else {
      obj[value] = copyObject ? {...item} : item;
    }
    if(item[children] && item[children].length) {
      item[children].map(func);
    }
  }
  items.forEach(func);
  return obj;
}

export function mapIds(items, idName = 'id') {
  return items.map((item) => item[idName]);
}

export function objectToFormData(obj) {
  if (!obj || !FormData) {
    return null;
  }

  let formData = new FormData();

  Object.keys(obj).forEach(key => {
    if (obj[key] instanceof Array) {
      obj[key].forEach(item => {
        formData.append(key, item)
      })
    } else {
      formData.append(key, obj[key])
    }
  });

  return formData;
}

export function fetchTimeout(ms, controller) {
  setTimeout(function() {
    controller?.abort?.()
  }, ms);
}

export function mergeCookies(fromReq = {}, fromStore = []) {
  let cookies = { ...fromReq };
  fromStore.forEach(item => {
    cookies = {
      ...cookies,
      [item.name]: item.value
    };
  });

  return Object.keys(cookies).map(key => cookie.serialize(key, cookies[key])).join('; ');
}

export function parseSetCookie(res) {
  const cookies = setCookieParser.parse(res, {
    decodeValues: true
  });
  if (cookies) {
    return cookies.map(cookie => ({
      ...cookie,
      expires: isNaN(cookie.expires) ? '' : cookie.expires
    }));
  }
}
